import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';

// Load all font candidates from Google Fonts on this page
const FONT_LINK = 'https://fonts.googleapis.com/css2?family=Syne:wght@500;600;700;800&family=Sora:wght@500;600;700;800&family=Bricolage+Grotesque:wght@500;600;700;800&family=Space+Grotesque:wght@500;600;700&family=Archivo:wght@500;600;700;800&family=Manrope:wght@500;600;700;800&family=Instrument+Serif&family=Fraunces:opsz,wght@9..144,500;9..144,700;9..144,800&family=Crimson+Pro:wght@600;700;800&family=DM+Serif+Display&display=swap';

const OPTIONS = [
  {
    id: 'syne',
    name: 'Syne',
    pair: 'Instrument Serif (italic accent)',
    display: 'Syne, sans-serif',
    accent: '"Instrument Serif", serif',
    mood: 'Current — geometric, contemporary, slightly editorial',
  },
  {
    id: 'bricolage',
    name: 'Bricolage Grotesque',
    pair: 'Fraunces (italic accent)',
    display: '"Bricolage Grotesque", sans-serif',
    accent: '"Fraunces", serif',
    mood: 'Variable, modern infrastructure — close to ABB tone',
  },
  {
    id: 'sora',
    name: 'Sora',
    pair: 'DM Serif Display',
    display: 'Sora, sans-serif',
    accent: '"DM Serif Display", serif',
    mood: 'Clean, neutral, tech-forward — Turner-adjacent',
  },
  {
    id: 'archivo',
    name: 'Archivo',
    pair: 'Crimson Pro',
    display: 'Archivo, sans-serif',
    accent: '"Crimson Pro", serif',
    mood: 'Compact, industrial, very readable headlines',
  },
  {
    id: 'manrope',
    name: 'Manrope',
    pair: 'Instrument Serif',
    display: 'Manrope, sans-serif',
    accent: '"Instrument Serif", serif',
    mood: 'Soft, humanist, friendly corporate',
  },
  {
    id: 'spacegrotesque',
    name: 'Space Grotesque',
    pair: 'Fraunces',
    display: '"Space Grotesque", sans-serif',
    accent: '"Fraunces", serif',
    mood: 'Technical, distinct, "engineering" feel',
  },
];

export default function Fonts() {
  const [picked, setPicked] = useState('syne');
  useEffect(() => {
    if (!document.querySelector('link[data-font-preview]')) {
      const l = document.createElement('link');
      l.rel = 'stylesheet'; l.href = FONT_LINK; l.setAttribute('data-font-preview','1');
      document.head.appendChild(l);
    }
  }, []);

  return (
    <main className="bg-[var(--cream)] min-h-screen">
      <section className="px-6 md:px-10 lg:px-14 pt-16 pb-10 max-w-[1500px] mx-auto">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">Font Options</div>
        <h1 className="font-display text-[clamp(34px,4.5vw,64px)] leading-[1.04] font-bold tracking-tight">
          Pick the heading font<br/><span className="font-serif-it text-[var(--orange)]">that fits Tricolite.</span>
        </h1>
        <p className="mt-6 text-[16px] text-[var(--text-mid)] max-w-2xl leading-relaxed">
          Each card below renders the same hero heading using a different display + serif-italic accent pairing.
          Pick one and tell me the ID — I'll apply it everywhere site-wide.
        </p>
      </section>

      <section className="px-6 md:px-10 lg:px-14 pb-24">
        <div className="max-w-[1500px] mx-auto space-y-5">
          {OPTIONS.map((o) => {
            const active = picked === o.id;
            return (
              <article
                key={o.id}
                onClick={() => setPicked(o.id)}
                className={`relative cursor-pointer bg-white rounded-3xl border transition-all p-8 md:p-14 ${active ? 'border-[var(--blue)] shadow-lg' : 'border-[var(--line)] hover:border-[var(--blue)]/40'}`}
              >
                <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-2">
                      Option · <span className="text-[var(--text-mid)]">{o.id}</span>
                    </div>
                    <div className="font-display font-bold text-[20px] heading-ink" style={{ color: '#0E1620' }}>
                      {o.name} <span className="text-[var(--text-light)] font-normal">+ {o.pair}</span>
                    </div>
                    <div className="text-[13px] text-[var(--text-mid)] mt-1">{o.mood}</div>
                  </div>
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-medium ${active ? 'bg-[var(--blue)] text-white' : 'border border-[var(--line)] text-[var(--text-mid)]'}`}>
                    {active && <Check className="w-4 h-4"/>}
                    {active ? 'Selected' : 'Click to preview'}
                  </div>
                </div>

                <div className="border-t border-[var(--line)] pt-8">
                  {/* H1-style preview */}
                  <div
                    className="font-bold tracking-tight"
                    style={{
                      fontFamily: o.display,
                      fontSize: 'clamp(40px, 6vw, 86px)',
                      lineHeight: 1.02,
                      color: '#2596BE',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Affordable, Clean &amp; Efficient
                    <br/>
                    <span style={{ fontFamily: o.accent, fontStyle: 'italic', color: '#F3763A', fontWeight: 400, letterSpacing: '-0.01em' }}>
                      Energy Solutions
                    </span>
                  </div>

                  {/* H2-style preview */}
                  <div
                    className="mt-10 font-bold tracking-tight"
                    style={{
                      fontFamily: o.display,
                      fontSize: 'clamp(26px, 3.6vw, 50px)',
                      lineHeight: 1.05,
                      color: '#2596BE',
                    }}
                  >
                    Decades of expertise in
                    <br/>
                    <span style={{ fontFamily: o.accent, fontStyle: 'italic', color: '#F3763A', fontWeight: 400 }}>
                      Electrical Solutions
                    </span>
                    {' '}& EPC Excellence
                  </div>

                  {/* small caps + body */}
                  <div className="mt-8 grid md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--text-light)] mb-3">Eyebrow style</div>
                      <div style={{ fontFamily: o.display, fontWeight: 700, fontSize: '14px', letterSpacing: '0.22em', textTransform: 'uppercase', color: '#F3763A' }}>
                        About · Our Story
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--text-light)] mb-3">Body remains Inter</div>
                      <p className="text-[15px] text-[var(--text-mid)] leading-[1.75]" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Body copy stays in Inter for readability. Only headlines and the italic accent change between options.
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-12 p-8 rounded-3xl bg-[var(--ink)] text-white max-w-[1500px] mx-auto">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-2">Currently previewing</div>
          <div className="font-display font-bold text-[28px] md:text-[34px]" style={{ color: '#fff' }}>
            {OPTIONS.find(x => x.id === picked).name} + {OPTIONS.find(x => x.id === picked).pair}
          </div>
          <div className="text-[14px] text-white/60 mt-2">Reply with the option ID — e.g. <code className="text-[var(--orange)]">"use bricolage"</code> — and I'll apply it across the site.</div>
        </div>
      </section>
    </main>
  );
}
